import { SubscriptionInterval } from 'common/interface';
import { addDays, addWeeks, addMonths, addYears } from 'date-fns';

interface CalculateEndDateParams {
  startDate: Date;
  interval: SubscriptionInterval;
  frequency?: number;
  expiryCount?: number;
}

const HALF_MOTH_DAYS = 15;
const MINUTES_PER_DAY = 1440;
const ANUAL_PERIODS = 12;
const EXPIRY_COUNT_DEFAULT = 12;

/**
 * Calculates the start date of a subscription plan.
 * @param trialPeriodDays - Number of trial days (default: 0)
 * @returns The calculated start date.
 */
export function calculateStartDate(trialPeriodDays: number = 0): Date {
  const startDate = new Date();
  return trialPeriodDays > 0 ? addDays(startDate, trialPeriodDays) : startDate;
}

/**
 * Calculates the end date of a subscription plan.
 * @param params - Object containing the startDate, interval, frequency, and expiryCount.
 * @returns The calculated end date.
 */
export function calculateEndDate({
  startDate,
  interval,
  frequency = 1,
  expiryCount = EXPIRY_COUNT_DEFAULT,
}: CalculateEndDateParams): Date {
  const intervalCount: number = Number(frequency) || 1;
  const periods: number = Number(expiryCount) || ANUAL_PERIODS;

  const intervalCalculations: Record<SubscriptionInterval, () => Date> = {
    day: () => addDays(startDate, intervalCount * periods),
    week: () => addWeeks(startDate, intervalCount * periods),
    half_month: () => addDays(startDate, HALF_MOTH_DAYS * intervalCount * periods),
    month: () => addMonths(startDate, intervalCount * periods),
    year: () => addYears(startDate, intervalCount * periods),
    minute: () => addDays(startDate, Math.ceil((intervalCount * periods) / MINUTES_PER_DAY)),
  };

  return (intervalCalculations[interval] || intervalCalculations.month)();
}
